import type { LoaderFunction } from '@remix-run/node'
import type { NavigationSimpleItem } from '@sporza/design-system/organisms/navigation-simple'

import { json, redirect } from '@remix-run/node'
import  { Link, Outlet, useLoaderData, useLocation, useMatches } from '@remix-run/react'
import { addAdUnit } from '@sporza/design-system/molecules/advertisement'
import Button, { ButtonVariant } from '@sporza/design-system/molecules/button'
import { Page } from '@sporza/design-system/organisms/page'
import { useTrackingStore } from '@sporza/tracking'
import { isSamePath , removeTrailingSlash } from '@sporza/utils/urls'
import { useEffect, useState } from 'react'

import { setHeaders, withErrorHandling } from '~/helpers'
import bffClient from '~/services/bff'

import { headers as teamHeaders, meta as teamMeta } from './nl.$namespace.$sport.spelers.$playerPath'

export const headers = teamHeaders

export const loader: LoaderFunction = withErrorHandling(async ({ request, params }) => {
  const {
    '*': contentPart,
    namespace,
    sport,
    teamPath
  } = params
  const requestUrl = new URL(request.url)
  requestUrl.pathname = `/nl/${namespace}/${sport}/ploegen/${teamPath}/`

  const content = contentPart && contentPart.endsWith('/') ? contentPart.slice(0, -1) : contentPart
  let contentType = content

  if (content && content.indexOf('~') > -1) {
    const [pathName, cType] = content.split('/')
    contentType = cType
    requestUrl.pathname += `${pathName}/`
  }

  const page = await bffClient.getInternalPage(requestUrl.pathname, 'aem', undefined, true)

  if (!page)
    throw new Response('Not Found', { status: 404 })

  if (!isSamePath(page?.permalink, requestUrl)) {
    const permalink = new URL(page.permalink)

    if (contentType) {
      permalink.pathname += `${contentType}/`
    }

    return redirect(permalink.toString(), 301)
  }

  const headers = setHeaders(page)

  return json({
    page,
    sport,
    teamBasePath: removeTrailingSlash(requestUrl.pathname),
    designSystemBaseUrl: process.env.DESIGN_SYSTEM_BASE_URL,
    apiBaseUrl: process.env.API_BASE_URL,
    content: contentType,
    bodyClassNames: page?.classNames
  }, { headers })
})

export const meta = teamMeta

const getNavigationItems = (teamBasePath: string, sport: string, contentType?: string): NavigationSimpleItem[] => {
  const menuItems = ['nieuws', 'video']

  if (sport === 'voetbal'){
    menuItems.push('wedstrijden')
    menuItems.push('klassement')
    menuItems.push('spelers')
    menuItems.push('clubinformatie')
  }

  return menuItems.map((item) => {
    return {
      label: item,
      component: <Button variant={ButtonVariant.tertiary}>
        <Link
          to={`${teamBasePath}/${item}/`}
          prefetch="intent"
          preventScrollReset
        >{item}</Link>
      </Button>,
      isActive: contentType === item || (!contentType && item === 'nieuws'),
    }
  })
}

export default function Slug() {
  const location = useLocation()
  const matches = useMatches()
  const currentMatch = matches[matches.length - 1]
  const data = useLoaderData<any>()
  const {
    page,
    sport,
    teamBasePath,
  } = data
  const {
    digitalData,
    ebaData,
    adData
  } = page

  const {
    '*': contentPart
  } = currentMatch.params || {}

  const initTracking = useTrackingStore((state) => state.initTracking)
  const [navigationItems, setNavigationItems] = useState<NavigationSimpleItem[]>(getNavigationItems(teamBasePath, sport, currentMatch.params.content))

  const content = contentPart && contentPart.endsWith('/') ? contentPart.slice(0, -1) : contentPart

  const contentType = content && content.indexOf('~') > -1
    ? content.split('/')[1]
    : content

  useEffect(() => {
    initTracking({ digitalData, ebaData })
    addAdUnit(adData)
    setNavigationItems(getNavigationItems(teamBasePath, sport, contentType))
  }, [location])

  return <Page
    {...page}
    hideAds={true}
    subNav={navigationItems}
  >
    <Outlet/>
  </Page>
}
